import { BrowserRouter as Router, Routes, Route, Outlet } from "react-router-dom";
import { lazy, Suspense } from "react";
import './App.css';
import "./i18n";
import Header from "./components/header/Header";
import { useTranslation } from "react-i18next";
import SessionManager from "./components/session-manager/SessionManager";
import ProtectedRoute from "./components/route-protection/ProtectedRoute";
import Loader from "./components/loader/Loader";
import AccountDeactivatedModal from "./components/account-deactivated/AccountDeactivatedModal";

// Lazy-loaded page components (code-splitting: each route is its own chunk).
const NurseLogin = lazy(() => import("./pages/nurse-module/login-screen/LoginScreen"));
const NurseForgotPass = lazy(() => import("./pages/nurse-module/forgot-password/ForgotPassword"));
const NurseResetPass = lazy(() => import("./pages/nurse-module/reset-password/ResetPassword"));
const NurseDashboard = lazy(() => import("./pages/nurse-module/dashboard/Dashboard"));
const NurseOrderManagement = lazy(() => import("./pages/nurse-module/nurse-order-management/NurseOrderManagement"));
const NurseSetting = lazy(() => import("./pages/nurse-module/setting/Setting"));
const NurseChangePassword = lazy(() => import("./pages/nurse-module/change-password/ChangePassword"));
const NurseChat = lazy(() => import("./pages/nurse-module/chat/NurseChat"));
const NurseChatList = lazy(() => import("./pages/nurse-module/chat-list/ChatList"));
const NurseProfile = lazy(() => import("./pages/nurse-module/Nurse-profile/NurseProfile"));
const NurseOnboarding = lazy(() => import("./pages/nurse-module/nurse-onboarding/NurseOnboarding"));
const NurseServicesAndPricing = lazy(() => import("./pages/nurse-module/services-pricing/ServicesAndPricing"));
const NurseEarningPayment = lazy(() => import("./pages/nurse-module/earning-payment/NurseEarningPayment"));
const NurseAppointmentDetail = lazy(() => import("./pages/nurse-module/nurse-appointment-detail/nurse_appointment_detail"));
const NurseManagement = lazy(() => import("./pages/nurse-module/nurse-management/NurseManagement"));
const NurseWithdrawFromWallet = lazy(() => import("./pages/nurse-module/withdraw-from-wallet/WithdrawFromWallet"));
const PublicNurseSessions = lazy(() => import("./pages/nurse-module/public-sessions/PublicNurseSessions"));
const PublicNurseAppointmentDetail = lazy(() => import("./pages/nurse-module/public-appointments/PublicNurseAppointmentDetail"));

function LayoutWithHeader({ changeLanguage }) {
  return (
    <>
      <Header changeLanguage={changeLanguage} />
      <Outlet /> {/* renders child routes */}
    </>
  );
}

function App() {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    document.dir = (lang === "ar") ? "rtl" : "ltr";
  };

  return (
    <SessionManager>
      <div className="App">
        <Router>
          <Suspense fallback={<Loader />}>
            <Routes>
              {/* Public routes - No authentication required */}
              <Route element={<LayoutWithHeader changeLanguage={changeLanguage} />}>
                <Route path="/" element={<NurseLogin />} />
                <Route path="/nurse_forgot" element={<NurseForgotPass />} />
                <Route path="/nurse_reset" element={<NurseResetPass />} />
              </Route>

              {/* Public assigned-nurse assessment page - no auth required */}
              <Route path="/public/sessions/:id" element={<PublicNurseSessions />} />

              {/* Public assigned-nurse appointment-details page (package orders) - no auth required */}
              <Route path="/public/appointment/:id" element={<PublicNurseAppointmentDetail />} />

              {/* Protected Nurse routes (role_id: 7) */}
              <Route path="/nurse_dashboard" element={<ProtectedRoute allowedRoleId={7} redirectTo="/"><NurseDashboard /></ProtectedRoute>} />
              <Route path="/nurse_ordermanagement" element={<ProtectedRoute allowedRoleId={7} redirectTo="/"><NurseOrderManagement /></ProtectedRoute>} />
              <Route path="/nurse_setting" element={<ProtectedRoute allowedRoleId={7} redirectTo="/"><NurseSetting /></ProtectedRoute>} />
              <Route path="/nurse_change_password" element={<ProtectedRoute allowedRoleId={7} redirectTo="/"><NurseChangePassword /></ProtectedRoute>} />
              <Route path="/nurse_chat" element={<ProtectedRoute allowedRoleId={7} redirectTo="/"><NurseChat /></ProtectedRoute>} />
              <Route path="/nurse_chatlist" element={<ProtectedRoute allowedRoleId={7} redirectTo="/"><NurseChatList /></ProtectedRoute>} />
              <Route path="/nurse_onboarding" element={<ProtectedRoute allowedRoleId={7} redirectTo="/" requireOnboarding={false}><NurseOnboarding /></ProtectedRoute>} />
              <Route path="/nurse_profile" element={<ProtectedRoute allowedRoleId={7} redirectTo="/"><NurseProfile /></ProtectedRoute>} />
              <Route path="/nurse_services" element={<ProtectedRoute allowedRoleId={7} redirectTo="/"><NurseServicesAndPricing /></ProtectedRoute>} />
              <Route path="/nurse_earning_payment" element={<ProtectedRoute allowedRoleId={7} redirectTo="/"><NurseEarningPayment /></ProtectedRoute>} />
              <Route path="/nurse_withdraw_from_wallet" element={<ProtectedRoute allowedRoleId={7} redirectTo="/"><NurseWithdrawFromWallet /></ProtectedRoute>} />
              <Route path="/nurse_management" element={<ProtectedRoute allowedRoleId={7} redirectTo="/"><NurseManagement /></ProtectedRoute>} />
              <Route path="/nurse_appointment_detail" element={<ProtectedRoute allowedRoleId={7} redirectTo="/"><NurseAppointmentDetail /></ProtectedRoute>} />
            </Routes>
          </Suspense>
          {/* App-wide blocking modal shown when the account is deactivated. */}
          <AccountDeactivatedModal />
        </Router>
      </div>
    </SessionManager>
  );
}

export default App;
